/* eslint-disable max-lines -- presenter window mirrors the audience controller UI (toolbar + interactive stage); splitting fragments the tool state machine */
/**
 * PresenterView
 *
 * Dual-pane layout rendered in the separate presenter window opened by
 * the master audience window. Displays — for the presenter's eyes only
 * — the current slide, a preview of what's coming next, the slide's
 * notes (real PPTX speaker notes), and a session timer.
 *
 * The presenter window is also the *controller*: it mirrors the audience
 * toolbar (laser, pen/draw, blank, thumbnails, prev/next, exit) so the
 * presenter can point and draw on the slide from their own screen and
 * have it appear on the audience display (#2153). Pointer, stroke, blank
 * and navigation events are streamed to the audience over a
 * BroadcastChannel; coordinates are normalized to the slide-image
 * "stage" so they line up across the two differently-shaped windows.
 *
 * Presentation state (slides + current index) still flows FROM the
 * audience via `init` / `state`; the presenter sends `jump` /
 * `laserMove` / `stroke` / `blank` / `end` back.
 *
 * All user-facing strings are routed through i18n under
 * `slideSense.present.*`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Stroke } from "./AnnotationCanvas"
import type { PresentationSyncApi, PresentationSyncMessage } from "./use-presentation-sync"
import type { BuilderSlide, SlideHotspot } from "../../types"

import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  MonitorOff,
  Pause,
  Pen,
  Play,
  Pointer,
  Trash2,
  Undo2,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { prefetchSlideImageBlob, useSlideImageBlob } from "../../hooks/use-slide-image-blob"
import {
  firstLinearIndex,
  lastLinearIndex,
  nextLinearIndex,
  prevLinearIndex,
  resolveHotspotJump,
} from "../../utils/presentation-nav-utils"
import { SlideThumbnailImage } from "../SlideThumbnailImage"
import { AnnotationCanvas } from "./AnnotationCanvas"
import { useImageAspect } from "./slide-stage-utils"
import { SlideLinkHotspots } from "./SlideLinkHotspots"
import { SlideStage, StageLaserDot } from "./SlideStage"
import { ThumbnailStrip } from "./ThumbnailStrip"
import { useSpeakerNotesBackfill } from "./use-speaker-notes-backfill"
import { usePresentationKeyboard } from "./usePresentationKeyboard"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PresenterViewProps {
  /** The sync API from `usePresentationSync` scoped to the active session. */
  sync: PresentationSyncApi
}

type PresenterMode = "nav" | "laser" | "pen"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimer(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

function getNotes(slide: BuilderSlide | undefined): string {
  if (!slide) return ""
  // Real PPTX speaker notes only — not an AI-generated stand-in. A slide
  // without authored notes shows the explicit empty state below, rather
  // than silently substituting the AI summary as if it were the presenter's
  // actual notes (see #2336).
  return (slide.slide.speaker_notes ?? "").trim()
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PresenterView({ sync }: PresenterViewProps) {
  const { t } = useTranslation()
  // --- Local mirror of master state ---------------------------------------
  const [slides, setSlides] = useState<BuilderSlide[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isMasterClosed, setIsMasterClosed] = useState(false)
  const [isReady, setIsReady] = useState(false)

  // --- Tool state (presenter is the controller) ---------------------------
  const [presenterMode, setPresenterMode] = useState<PresenterMode>("nav")
  const [strokesBySlide, setStrokesBySlide] = useState<Map<string, Stroke[]>>(() => new Map())
  const [audienceBlank, setAudienceBlank] = useState(false)
  const [localLaser, setLocalLaser] = useState<{ x: number; y: number } | null>(null)
  const [showThumbs, setShowThumbs] = useState(false)
  const lastViewedIndexRef = useRef<number | null>(null)
  const { aspect, onImgLoad } = useImageAspect()

  // --- Timer ---------------------------------------------------------------
  const [startedAt] = useState(() => Date.now())
  const [pausedAt, setPausedAt] = useState<number | null>(null)
  const [accumulatedPause, setAccumulatedPause] = useState(0)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (pausedAt !== null) {
      // While paused, elapsedMs is computed from pausedAt (constant), so
      // ticking nowMs only forces wasted re-renders.
      return
    }
    const tick = () => setNowMs(Date.now())
    const interval = window.setInterval(tick, 500)
    return () => window.clearInterval(interval)
  }, [pausedAt])

  const elapsedMs = useMemo(() => {
    if (pausedAt !== null) {
      return pausedAt - startedAt - accumulatedPause
    }
    return nowMs - startedAt - accumulatedPause
  }, [pausedAt, startedAt, accumulatedPause, nowMs])

  const toggleTimer = useCallback(() => {
    setPausedAt((prev) => {
      if (prev === null) {
        // Pausing.
        return Date.now()
      }
      // Resuming — add the paused interval to the accumulator.
      setAccumulatedPause((acc) => acc + (Date.now() - prev))
      return null
    })
  }, [])

  // --- Subscribe to master broadcast --------------------------------------
  //
  // We subscribe FIRST and then publish `hello` so the master's `init`
  // response is guaranteed to land on a wired-up listener. Without the
  // hello handshake we'd depend on the master's first-mount `init`
  // firing after we subscribe — but BroadcastChannel does not buffer,
  // so any `init` broadcast before subscribe is lost and the presenter
  // hangs on "Väntar på presentationen…".
  //
  // Belt-and-suspenders: even with the subscribe-before-publish order,
  // the master side may still be mid-mount when the presenter's first
  // `hello` goes out (e.g. slow React reconciliation after the popup's
  // initial paint), and the master's `subscribe` effect hasn't attached
  // its listener yet. To make this race-immune, retry `hello` every 500ms
  // for up to 10s, stopping as soon as any non-hello message lands.
  const readyRef = useRef(false)
  useEffect(() => {
    readyRef.current = false
    let helloAttempts = 0
    const MAX_HELLO_ATTEMPTS = 20 // 20 * 500ms = 10s
    const unsubscribe = sync.subscribe((msg: PresentationSyncMessage) => {
      switch (msg.type) {
        case "init":
          readyRef.current = true
          setSlides(msg.slides)
          setCurrentIndex(msg.currentIndex)
          lastViewedIndexRef.current = msg.lastViewedIndex
          // The audience is the source of truth for blank — it unblanks on
          // navigation. Mirror it so the presenter's badge/toggle don't go
          // stale (#2153 review).
          setAudienceBlank(msg.mode === "blank")
          setIsReady(true)
          setIsMasterClosed(false)
          return
        case "state":
          readyRef.current = true
          setCurrentIndex(msg.currentIndex)
          lastViewedIndexRef.current = msg.lastViewedIndex
          setAudienceBlank(msg.mode === "blank")
          return
        case "closed":
          setIsMasterClosed(true)
          return
        default:
          return
      }
    })
    // Fire the first hello immediately — usually this single message is
    // enough and the retry loop below is a no-op.
    sync.publish({ type: "hello" })
    const retryTimer = window.setInterval(() => {
      if (readyRef.current || helloAttempts >= MAX_HELLO_ATTEMPTS) {
        window.clearInterval(retryTimer)
        return
      }
      helloAttempts += 1
      sync.publish({ type: "hello" })
    }, 500)
    return () => {
      window.clearInterval(retryTimer)
      unsubscribe()
    }
  }, [sync])

  // --- Announce "closed" on unload so master can react -------------------
  useEffect(() => {
    const onBeforeUnload = () => {
      sync.publish({ type: "closed" })
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload)
    }
  }, [sync])

  // --- Derived values -----------------------------------------------------
  const current: BuilderSlide | undefined = slides.length > 0 ? slides[currentIndex] : undefined
  const nextIndex = current ? nextLinearIndex(slides, currentIndex) : 0
  const next: BuilderSlide | undefined =
    current && nextIndex !== currentIndex ? slides[nextIndex] : undefined
  const currentSlideId = current ? current.slide.id : undefined
  const totalLabel =
    slides.length === 0
      ? "–"
      : t("slideSense.present.slideCounter", {
          current: currentIndex + 1,
          total: slides.length,
        })
  const storedNotes = useMemo(() => getNotes(current), [current])
  const notes = useSpeakerNotesBackfill(current?.slide.id, storedNotes)
  const currentStrokes = useMemo<Stroke[]>(
    () => (currentSlideId ? (strokesBySlide.get(currentSlideId) ?? []) : []),
    [currentSlideId, strokesBySlide]
  )

  // Auth-aware blob loader — a plain `<img src>` 401s in local dev because
  // the Bearer token doesn't ride on resource loads (#2153).
  const { url: currentUrl, error: imageError } = useSlideImageBlob(
    current ? current.slide.id : undefined
  )

  // --- Navigation ---------------------------------------------------------
  const goTo = useCallback(
    (index: number) => {
      if (slides.length === 0) return
      const clamped = Math.max(0, Math.min(index, slides.length - 1))
      setCurrentIndex(clamped)
      sync.publish({
        type: "jump",
        index: clamped,
        lastViewedIndex: lastViewedIndexRef.current,
      })
    },
    [slides.length, sync]
  )

  const goNext = useCallback(
    () => goTo(nextLinearIndex(slides, currentIndex)),
    [currentIndex, goTo, slides]
  )
  const goPrev = useCallback(
    () => goTo(prevLinearIndex(slides, currentIndex)),
    [currentIndex, goTo, slides]
  )

  const activateHotspot = useCallback(
    (hotspot: SlideHotspot) => {
      const target = resolveHotspotJump(slides, hotspot, {
        currentIndex,
        lastViewedIndex: lastViewedIndexRef.current,
      })
      if (target === null || target === currentIndex) return
      if (hotspot.kind === "slide") lastViewedIndexRef.current = currentIndex
      goTo(target)
    },
    [currentIndex, goTo, slides]
  )

  // Clear the laser dot when the slide changes — a stale dot on the new
  // slide would point at the wrong thing.
  useEffect(() => {
    setLocalLaser(null)
  }, [currentIndex])

  // Warm the blob cache for the upcoming slides, matching the audience's
  // preload. Symmetric prefetch keeps the two windows' images (and the
  // stage aspect they derive on load) in step across navigation (#2153
  // review).
  useEffect(() => {
    if (slides.length === 0) return
    for (const i of [currentIndex + 1, currentIndex + 2]) {
      if (i < slides.length) prefetchSlideImageBlob(slides[i]?.slide.id)
    }
  }, [currentIndex, slides])

  // --- Tool handlers ------------------------------------------------------
  const setMode = useCallback(
    (m: PresenterMode) => {
      setPresenterMode((prev) => {
        if (prev === "laser" && m !== "laser") {
          // Leaving laser — hide the dot on both sides.
          setLocalLaser(null)
          sync.publish({ type: "laserOff" })
        }
        return m
      })
    },
    [sync]
  )

  const toggleLaser = useCallback(
    () => setMode(presenterMode === "laser" ? "nav" : "laser"),
    [presenterMode, setMode]
  )
  const togglePen = useCallback(
    () => setMode(presenterMode === "pen" ? "nav" : "pen"),
    [presenterMode, setMode]
  )
  const toggleThumbs = useCallback(() => setShowThumbs((v) => !v), [])

  const toggleBlank = useCallback(() => {
    setAudienceBlank((b) => {
      const nextBlank = !b
      sync.publish({ type: "blank", blank: nextBlank })
      return nextBlank
    })
  }, [sync])

  const handleAddStroke = useCallback(
    (stroke: Stroke) => {
      if (!currentSlideId) return
      setStrokesBySlide((prev) => {
        const nextMap = new Map(prev)
        const existing = nextMap.get(currentSlideId) ?? []
        nextMap.set(currentSlideId, [...existing, stroke])
        return nextMap
      })
      sync.publish({ type: "stroke", slideId: currentSlideId, stroke })
    },
    [currentSlideId, sync]
  )

  const clearCurrentStrokes = useCallback(() => {
    if (!currentSlideId) return
    setStrokesBySlide((prev) => {
      if (!prev.has(currentSlideId)) return prev
      const nextMap = new Map(prev)
      nextMap.delete(currentSlideId)
      return nextMap
    })
    sync.publish({ type: "clearStrokes", slideId: currentSlideId })
  }, [currentSlideId, sync])

  const undoLastStroke = useCallback(() => {
    if (!currentSlideId) return
    setStrokesBySlide((prev) => {
      const existing = prev.get(currentSlideId) ?? []
      if (existing.length === 0) return prev
      const nextMap = new Map(prev)
      nextMap.set(currentSlideId, existing.slice(0, -1))
      return nextMap
    })
    sync.publish({ type: "undoStroke", slideId: currentSlideId })
  }, [currentSlideId, sync])

  // Keyboard wrappers — pen-only actions are no-ops outside pen mode so
  // the E / U / Ctrl+Z keys don't silently mutate during navigation.
  const handleClearKey = useCallback(() => {
    if (presenterMode === "pen") clearCurrentStrokes()
  }, [presenterMode, clearCurrentStrokes])
  const handleUndoKey = useCallback(() => {
    if (presenterMode === "pen") undoLastStroke()
  }, [presenterMode, undoLastStroke])

  // --- Laser capture (over the slide stage) -------------------------------
  const handleLaserMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
      setLocalLaser({ x, y })
      sync.publish({ type: "laserMove", x, y })
    },
    [sync]
  )
  const handleLaserLeave = useCallback(() => {
    setLocalLaser(null)
    sync.publish({ type: "laserOff" })
  }, [sync])

  // --- Exit ---------------------------------------------------------------
  // Ending from the presenter window tells the audience to tear the whole
  // presentation down (which also closes this window), then we close
  // ourselves as a fallback.
  const endPresentation = useCallback(() => {
    sync.publish({ type: "end" })
    // Defer the self-close to a later task so the `end` broadcast actually
    // flushes first. `BroadcastChannel.postMessage` delivers asynchronously;
    // closing the window synchronously here would tear down the channel
    // before `end` is dispatched, and the audience (whose `end` handler is
    // the only path that exits fullscreen) could be left stranded — the
    // exact dual-window failure this PR targets (#2153 review).
    window.setTimeout(() => {
      try {
        window.close()
      } catch {
        // Ignore — the audience's `end` handler will close us.
      }
    }, 0)
  }, [sync])

  // Esc priority mirrors the audience: first Esc drops pen/laser back to
  // nav, a second Esc ends the presentation.
  const handleExitKey = useCallback(() => {
    if (presenterMode !== "nav") {
      setMode("nav")
      return
    }
    endPresentation()
  }, [presenterMode, setMode, endPresentation])

  // --- Keyboard shortcuts (mirror the master window) ---------------------
  usePresentationKeyboard({
    enabled: isReady && !isMasterClosed,
    onBlank: toggleBlank,
    onClearStrokes: handleClearKey,
    onEnd: () => goTo(lastLinearIndex(slides)),
    onExit: handleExitKey,
    onHome: () => goTo(firstLinearIndex(slides)),
    onJump: (n) => goTo(n - 1),
    onNext: goNext,
    onPrev: goPrev,
    onToggleLaser: toggleLaser,
    onTogglePen: togglePen,
    onToggleThumbs: toggleThumbs,
    onUndoStroke: handleUndoKey,
  })

  // --- Render -------------------------------------------------------------

  // "Waiting for connection" state — we've mounted but haven't heard
  // from the master yet. Happens during the brief window between the
  // popup opening and the audience window publishing `init`.
  if (!isReady && !isMasterClosed) {
    return (
      <div
        className="bg-background text-foreground flex h-screen w-screen items-center justify-center"
        data-testid="presenter-view-waiting"
        role="status"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="border-primary h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground text-sm">
            {t("slideSense.present.waitingForPresentation")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={400}>
      <div
        aria-label={t("slideSense.present.presenterRegion")}
        className="bg-background text-foreground flex h-screen w-screen flex-col"
        data-testid="presenter-view"
        role="region"
      >
        {/* Header — session timer, slide counter, timer control */}
        <header
          className="border-border bg-card flex shrink-0 items-center justify-between gap-4 border-b px-6 py-3"
          data-testid="presenter-view-header"
        >
          <div className="flex items-center gap-6">
            <div>
              <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
                {t("slideSense.present.timer")}
              </p>
              <p className="font-mono text-xl tabular-nums" data-testid="presenter-view-timer">
                {formatTimer(elapsedMs)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
                {t("slideSense.present.slideLabel")}
              </p>
              <p className="font-mono text-xl tabular-nums" data-testid="presenter-view-counter">
                {totalLabel}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="presenter-view-toggle-timer"
              size="sm"
              variant="outline"
              aria-label={
                pausedAt === null
                  ? t("slideSense.present.pauseTimer")
                  : t("slideSense.present.resumeTimer")
              }
              onClick={toggleTimer}
            >
              {pausedAt === null ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span className="ml-2 text-sm">
                {pausedAt === null ? t("slideSense.present.pause") : t("slideSense.present.resume")}
              </span>
            </Button>
          </div>
        </header>

        {/* Banner — audience window closed */}
        {isMasterClosed && (
          <div
            className="bg-destructive/10 text-destructive border-destructive/30 flex shrink-0 items-center justify-center gap-2 border-b px-4 py-2 text-sm"
            data-testid="presenter-view-closed-banner"
            role="status"
          >
            <X className="h-4 w-4" />
            <span>{t("slideSense.present.endedDetail")}</span>
          </div>
        )}

        {/* Body — 60/40 split: current slide on the left, next + notes on the right */}
        <div className="flex min-h-0 flex-1">
          {/* Left pane — current slide + interactive stage */}
          <div
            className="relative min-h-0 flex-[3] bg-black"
            data-testid="presenter-view-current-pane"
          >
            <SlideStage
              aspect={aspect}
              className="absolute inset-0"
              data-testid="presenter-view-stage"
            >
              {currentUrl && (
                <img
                  className="absolute inset-0 h-full w-full object-contain"
                  data-testid="presenter-view-current-image"
                  src={currentUrl}
                  alt={
                    current
                      ? current.slide.ai_title ||
                        t("slideSense.present.slideFallback", { n: currentIndex + 1 })
                      : ""
                  }
                  onLoad={onImgLoad}
                />
              )}
              <SlideLinkHotspots
                enabled={presenterMode === "nav"}
                jumpLabel={(n) => t("slideSense.present.jumpToSlide", { n })}
                lastViewedLabel={t("slideSense.present.returnToLastSlide")}
                links={current?.slide.links ?? []}
                onActivate={activateHotspot}
              />
              {/* Loading shows nothing (black pane); only flag a true failure. */}
              {!currentUrl && imageError && (
                <div className="text-muted-foreground absolute inset-0 flex items-center justify-center text-sm">
                  {t("slideSense.present.imageUnavailable")}
                </div>
              )}

              {/* Pen overlay — captures drags while pen mode is active, always
                renders committed strokes so they persist across slide flips. */}
              {currentSlideId && (
                <AnnotationCanvas
                  active={presenterMode === "pen"}
                  slideId={currentSlideId}
                  strokes={currentStrokes}
                  onAddStroke={handleAddStroke}
                />
              )}

              {/* Laser capture — transparent layer that tracks the cursor and
                streams its normalized position to the audience. */}
              {presenterMode === "laser" && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  data-testid="presenter-view-laser-capture"
                  style={{ cursor: "none", zIndex: 55 }}
                  onMouseLeave={handleLaserLeave}
                  onMouseMove={handleLaserMove}
                />
              )}

              {/* The presenter's own view of the laser dot. */}
              {localLaser && <StageLaserDot x={localLaser.x} y={localLaser.y} />}
            </SlideStage>

            {/* Indicator — the audience screen is currently blanked. */}
            {audienceBlank && (
              <div
                className="pointer-events-none absolute top-3 left-3 z-[70] rounded-md bg-black/70 px-2 py-1 text-xs text-white"
                data-testid="presenter-view-audience-blanked"
                role="status"
              >
                {t("slideSense.present.audienceBlanked")}
              </div>
            )}

            {/* Thumbnail strip — overlays the slide for quick jumps. */}
            <ThumbnailStrip
              currentIndex={currentIndex}
              slides={slides}
              visible={showThumbs}
              onJump={goTo}
            />
          </div>

          {/* Right pane — next slide preview + notes */}
          <aside
            className="border-border bg-card flex min-h-0 flex-[2] flex-col border-l"
            data-testid="presenter-view-side-pane"
          >
            {/* Next slide preview */}
            <div className="border-border shrink-0 border-b p-4">
              <p className="text-muted-foreground mb-2 text-[11px] font-semibold tracking-wider uppercase">
                {t("slideSense.present.nextSlide")}
              </p>
              {next ? (
                <div
                  className="bg-muted/40 aspect-[16/9] w-full overflow-hidden rounded-lg"
                  data-testid="presenter-view-next-thumb"
                >
                  <SlideThumbnailImage
                    highRes
                    slideId={next.slide.id}
                    thumbnailUrl={next.slide.thumbnail_url}
                    alt={
                      next.slide.ai_title ||
                      t("slideSense.present.slideFallback", { n: currentIndex + 2 })
                    }
                  />
                </div>
              ) : (
                <div
                  className="bg-muted/40 text-muted-foreground flex aspect-[16/9] w-full items-center justify-center rounded-lg text-sm"
                  data-testid="presenter-view-next-empty"
                >
                  {t("slideSense.present.lastSlide")}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="flex min-h-0 flex-1 flex-col p-4">
              <p className="text-muted-foreground mb-2 shrink-0 text-[11px] font-semibold tracking-wider uppercase">
                {t("slideSense.present.notes")}
              </p>
              <ScrollArea className="min-h-0 flex-1">
                {notes.length > 0 ? (
                  <p
                    className="text-foreground text-sm leading-relaxed whitespace-pre-wrap"
                    data-testid="presenter-view-notes"
                  >
                    {notes}
                  </p>
                ) : (
                  <p
                    className="text-muted-foreground text-sm italic"
                    data-testid="presenter-view-notes-empty"
                  >
                    {t("slideSense.present.notesEmpty")}
                  </p>
                )}
              </ScrollArea>
            </div>
          </aside>
        </div>

        {/* Footer — controller toolbar (mirrors the audience toolbar) */}
        <footer
          className="border-border bg-card flex shrink-0 items-center justify-center gap-1 border-t px-6 py-3"
          data-testid="presenter-view-footer"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.toggleThumbnails")}
                aria-pressed={showThumbs}
                className={showThumbs ? "border-foreground bg-accent border" : ""}
                data-testid="presenter-view-thumbs"
                size="icon"
                variant="ghost"
                onClick={toggleThumbs}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("slideSense.present.toggleThumbnails")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.modeLaser")}
                aria-pressed={presenterMode === "laser"}
                className={presenterMode === "laser" ? "border-foreground bg-accent border" : ""}
                data-testid="presenter-view-laser"
                size="icon"
                variant="ghost"
                onClick={toggleLaser}
              >
                <Pointer className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("slideSense.present.modeLaser")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.modePen")}
                aria-pressed={presenterMode === "pen"}
                className={presenterMode === "pen" ? "border-foreground bg-accent border" : ""}
                data-testid="presenter-view-pen"
                size="icon"
                variant="ghost"
                onClick={togglePen}
              >
                <Pen className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("slideSense.present.modePen")}</TooltipContent>
          </Tooltip>
          {presenterMode === "pen" && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("slideSense.present.undoStroke")}
                    data-testid="presenter-view-undo"
                    size="icon"
                    variant="ghost"
                    onClick={undoLastStroke}
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("slideSense.present.undoStroke")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("slideSense.present.clearStrokes")}
                    data-testid="presenter-view-clear"
                    size="icon"
                    variant="ghost"
                    onClick={clearCurrentStrokes}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("slideSense.present.clearStrokes")}</TooltipContent>
              </Tooltip>
            </>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.blankScreen")}
                aria-pressed={audienceBlank}
                className={audienceBlank ? "border-foreground bg-accent border" : ""}
                data-testid="presenter-view-blank"
                size="icon"
                variant="ghost"
                onClick={toggleBlank}
              >
                <MonitorOff className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("slideSense.present.blankScreen")}</TooltipContent>
          </Tooltip>
          <span aria-hidden className="bg-border mx-1 h-6 w-px" />
          <Button
            aria-label={t("slideSense.present.prev")}
            data-testid="presenter-view-prev"
            disabled={prevLinearIndex(slides, currentIndex) === currentIndex || slides.length === 0}
            size="icon"
            variant="ghost"
            onClick={goPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span
            className="text-muted-foreground min-w-[4rem] text-center text-sm"
            data-testid="presenter-view-footer-counter"
          >
            {totalLabel}
          </span>
          <Button
            aria-label={t("slideSense.present.next")}
            data-testid="presenter-view-next"
            disabled={nextIndex === currentIndex || slides.length === 0}
            size="icon"
            variant="ghost"
            onClick={goNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span aria-hidden className="bg-border mx-1 h-6 w-px" />
          <Button
            aria-label={t("slideSense.present.exitAria")}
            className="gap-2"
            data-testid="presenter-view-exit"
            size="sm"
            variant="ghost"
            onClick={endPresentation}
          >
            <X className="h-4 w-4" />
            <span className="text-sm">{t("slideSense.present.exit")}</span>
          </Button>
        </footer>
      </div>
    </TooltipProvider>
  )
}
